import { Box, Dialog, DialogContent, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomTypography from "../../../Components/Typography";
// import CustomTooltip from "../../../Components/CustomTooltip";
// import { useSnackbar } from "../../../Components/SnackBar";
import { useTranslation } from "react-i18next";

const SupplierAcceptedModal = (props) => {
  const {
    supplierProfileData,
    selectedPricelists,
    openSupplierAccept,
    handleCloseSupplierAcceptModal,
    // Everything approved as { name, kind }, already localized, across all
    // three verticals. Approving is one action now, so the confirmation has to
    // name the store and menu categories too — deriving from `pricelists` alone
    // left a products-only approval reading "…offers ." with nothing in it.
    // `kind` also decides the noun: services, products, or both.
    acceptedItems,
  } = props;

  // const showSnackbar = useSnackbar();
  const [selectedPricelistsNames, setSelectedPricelistsNames] = useState([]);
  const { pricelists, supplier } = supplierProfileData;

  const { t } = useTranslation(["supplier-accepted-modal", "categories"]);

  useEffect(() => {
    // Prefer the caller's list when it supplied one; fall back to deriving the
    // service pricelists, which is all the older single-vertical path had.
    if (acceptedItems?.length) {
      setSelectedPricelistsNames(acceptedItems.map((i) => i.name));
      return;
    }
    setSelectedPricelistsNames(() =>
      pricelists
        .filter((p) => selectedPricelists.includes(p.subcategory_id))
        .map((p) => t(p.subcategory_name, { ns: "categories" }))
    );
    //eslint-disable-next-line
  }, [selectedPricelists, pricelists, acceptedItems]);

  /**
   * What the supplier offers, capped.
   *
   * This is a blurb the manager pastes to their members, so it stays short and
   * bounded — a supplier with 18 approved categories produced a 375-character
   * run-on. Listing everything was also ambiguous: some category names contain
   * commas of their own ("שיפוצים, בנייה וגמר"), so in a comma-joined list you
   * cannot tell where one ends and the next begins.
   *
   * "וגם" joins the final pair ONLY when the list is complete. With a
   * remainder the sentence already ends in "ועוד N", and "…, ב וגם ג ועוד 15"
   * reads as two conjunctions fighting each other.
   */
  /**
   * The noun the sentence leads with, built from the verticals actually
   * approved: services, products, or both. Calling a store category a "service"
   * was wrong the moment approval stopped being services-only.
   *
   * Hebrew takes the definite article on every noun and attaches the
   * conjunction to the following word — "השירותים והמוצרים" — so the join is
   * " ו" between already-prefixed nouns, with a comma before the last of three.
   */
  /**
   * Singular/plural forms are chosen here rather than through i18next's
   * `count`. Hebrew has four plural categories (one/two/many/other), so
   * `count: 2` resolves to a `_two` form that this copy has no reason to
   * define, and a missing form renders the raw key. Two explicit forms is all
   * the sentence needs, in either language.
   */
  const KIND_ORDER = ["service", "product", "restaurant"];
  const offeredNoun = (() => {
    const kinds = KIND_ORDER.filter((k) =>
      (acceptedItems || []).some((i) => i.kind === k),
    );
    // No kinds means the legacy services-only path fed us bare names.
    const present = kinds.length ? kinds : ["service"];
    const plural = present.length > 1 || selectedPricelistsNames.length > 1;
    const form = plural ? "many" : "one";
    const nouns = present.map((k) => t(`noun_${k}_${form}`));
    const joined =
      nouns.length === 1
        ? nouns[0]
        : `${nouns.slice(0, -1).join(", ")} ${t("noun_join")}${nouns[nouns.length - 1]}`;
    // English carries one article for the whole phrase ("The services and
    // products"); Hebrew takes it on every noun, so its prefix is empty and the
    // article lives in the noun keys themselves.
    return `${t("noun_prefix")}${joined} ${t(`offered_${form}`)}`;
  })();

  const MAX_LISTED = 3;
  const offeredSummary = (() => {
    const names = selectedPricelistsNames;
    if (!names.length) return "";
    const shown = names.slice(0, MAX_LISTED);
    const rest = names.length - shown.length;
    if (rest > 0) {
      // No "וגם" — "ועוד N" is the closer.
      return `${shown.join(", ")} ${
        rest === 1 ? t("more_one") : t("more_many", { count: rest })
      }`;
    }
    if (shown.length === 1) return shown[0];
    return `${shown.slice(0, -1).join(", ")} ${t("and", {
      ns: "supplier-accepted-modal",
    })} ${shown[shown.length - 1]}`;
  })();

  // const supplierProfileUrl = "www.commy.com/community_homepage";
  return (
    <Dialog
      open={openSupplierAccept}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCloseSupplierAcceptModal();
      }}
      disableEscapeKeyDown
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          px: { xs: "16px", md: "24px" },
          py: "18px",
          borderBottom: "1px solid #EAEAEA",
        }}
      >
        <CustomTypography
          type="subheading"
          props={{
            color: "#3E3F4C",
            lineHeight: "28px",
          }}
        >
          {t("supplier_accepted")}
        </CustomTypography>

        <IconButton
          onClick={handleCloseSupplierAcceptModal}
          sx={{ padding: "6px" }}
        >
          <img src="/cancel_icon.svg" alt="close" width={14} height={14} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: "16px", md: "24px" } }}>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ maxWidth: "508px", width: "100%" }}
        >
          <Box
            sx={{
              height: "126px",
              width: "136px",
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box sx={{ position: "absolute", height: "126px", width: "136px" }}>
              <img
                src="/supplier-accept-modal-img-group.svg"
                alt="supplier_accept_modal_img"
                width="136px"
                height="126px"
              />
            </Box>

            <img
              src={supplier.display_image_url}
              alt="image_url"
              width="100px"
              height="100px"
              style={{ borderRadius: "100%" }}
            />
          </Box>

          <CustomTypography
            type="subheading"
            props={{
              color: "#3E3F4C",
              lineHeight: "28px",
              my: "16px",
              textAlign: "center",
            }}
          >
            <span style={{ fontWeight: 600 }}>
              {supplier.name}
            </span>{" "}
            {t("start_selling_in_community")}
          </CustomTypography>

          <CustomTypography
            props={{
              color: "#3E3F4C",
              fontSize: "15px",
              lineHeight: "160%",
              fontWeight: 400,
              mb: "24px",
            }}
          >
            {t("share_with_community_members")}
          </CustomTypography>

          <Box
            sx={{
              background: "#E7F6EE",
              border: "1px dashed #3E3F4C",
              p: { xs: "16px", md: "24px" },
              borderRadius: "12px",
            }}
          >
            <CustomTypography
              type="subheading"
              props={{
                color: "#3E3F4C",
                lineHeight: "160%",
              }}
            >
              {supplier.name} {t("supplier_available_address")}{" "}
              {supplier.address}! {offeredNoun}{" "}
              {offeredSummary}.
            </CustomTypography>

            {/* <Box sx={{ mt: "12px" }}>
              <CustomTypography
                props={{
                  color: "#3E3F4C",
                  fontSize: "15px",
                  lineHeight: "160%",
                  fontWeight: 400,
                }}
              >
                {t("check_out_offer")}
              </CustomTypography>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="8px"
              >
                <CustomTypography
                  props={{
                    color: "#3E3F4C",
                    fontSize: "15px",
                    lineHeight: "160%",
                    fontWeight: 400,
                    wordBreak: "break-word",
                    wordWrap: "break-word",
                  }}
                >
                  {supplierProfileUrl}
                </CustomTypography>

                <CustomTooltip
                  title="Copy"
                  tooltipSxStyles={{ paddingY: "5px" }}
                >
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(supplierProfileUrl);
                      showSnackbar(t("url_copied"), "success");
                    }}
                  >
                    <img src="/file_copy_icon.svg" alt="file_copy_icon" />
                  </IconButton>
                </CustomTooltip>
              </Box>
            </Box> */}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierAcceptedModal;
